import React, { useEffect, useMemo, useState } from "react";
import api from "../../api";
import Select from "react-select";
import {
  FaUserTie,
  FaChalkboardTeacher,
  FaSave,
  FaEdit,
  FaTrash,
  FaSyncAlt,
  FaSearch,
  FaUser,
  FaMobileAlt,
  FaIdCard,
  FaTimes,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./ClassTeacherPage.css";

/*
=========================================================
 CLASS TEACHER PAGE
=========================================================

 API ENDPOINTS:

 GET  /class-teacher/classes
 GET  /class-teacher/sections?class_id=XX
 GET  /class-teacher/teachers
 GET  /class-teacher/assignment?class_id=XX&section_id=XX
 GET  /class-teacher/assignments
 POST /class-teacher/save
 POST /class-teacher/remove

 Academic year is NOT shown in frontend.
 Backend should automatically use current active academic year.
=========================================================
*/

const ClassTeacherPage = () => {
  // =====================================================
  // FORM
  // =====================================================

  const [form, setForm] = useState({
    class_id: null,
    section_id: null,
    teacher_id: null,
  });

  // =====================================================
  // DATA
  // =====================================================

  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [assignments, setAssignments] = useState([]);

  // =====================================================
  // LOADING
  // =====================================================

  const [loadingClasses, setLoadingClasses] = useState(false);
  const [loadingSections, setLoadingSections] = useState(false);
  const [loadingTeachers, setLoadingTeachers] = useState(false);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  // =====================================================
  // SEARCH
  // =====================================================

  const [teacherSearch, setTeacherSearch] = useState("");

  // =====================================================
  // EDIT MODE
  // =====================================================

  const [editingAssignment, setEditingAssignment] = useState(null);

  // =====================================================
  // HELPERS
  // =====================================================

  const showToast = (type, message) => {
    toast[type](message, {
      position: "top-right",
      autoClose: 2200,
      hideProgressBar: false,
    });
  };

  const getValue = (obj, keys, fallback = "") => {
    for (const key of keys) {
      if (
        obj &&
        obj[key] !== undefined &&
        obj[key] !== null &&
        obj[key] !== ""
      ) {
        return obj[key];
      }
    }

    return fallback;
  };

  const getArray = (response) => {
    if (!response || !response.data) return [];

    const data = response.data;

    if (Array.isArray(data)) {
      return data;
    }

    if (Array.isArray(data.data)) {
      return data.data;
    }

    if (Array.isArray(data.rows)) {
      return data.rows;
    }

    if (Array.isArray(data.result)) {
      return data.result;
    }

    return [];
  };

  const isSuccess = (response) => {
    if (!response || !response.data) return false;

    if (response.data.success === false) {
      return false;
    }

    return true;
  };

  // =====================================================
  // NORMALIZE TEACHER
  // =====================================================

  const normalizeTeacher = (teacher) => {
    const id = getValue(
      teacher,
      [
        "id",
        "teacher_id",
        "staff_id",
        "user_id",
        "employee_id",
        "teacherId",
      ],
      ""
    );

    const name = getValue(
      teacher,
      [
        "name",
        "teacher_name",
        "staff_name",
        "full_name",
        "employee_name",
        "teacherName",
      ],
      "Unknown Teacher"
    );

    const mobile = getValue(
      teacher,
      [
        "mobile",
        "mobile_no",
        "mobile_number",
        "phone",
        "phone_no",
        "contact_no",
        "contact",
      ],
      ""
    );

    const teacherCode = getValue(
      teacher,
      [
        "teacher_code",
        "teacher_id_no",
        "employee_code",
        "employee_id",
        "staff_code",
        "code",
      ],
      id
    );

    const designation = getValue(
      teacher,
      [
        "designation",
        "designation_name",
        "post",
        "role_name",
      ],
      "Teacher"
    );

    const photo = getValue(
      teacher,
      [
        "photo",
        "photo_url",
        "profile_photo",
        "profile_image",
        "image",
        "image_url",
        "teacher_photo",
        "staff_photo",
        "avatar",
      ],
      ""
    );

    return {
      ...teacher,
      id,
      name,
      mobile,
      teacherCode,
      designation,
      photo,
    };
  };

  // =====================================================
  // TEACHER PHOTO
  // =====================================================

  const getTeacherPhoto = (teacher) => {
    if (!teacher) return "";

    const photo = teacher.photo;

    if (!photo) return "";

    // Already absolute URL
    if (
      photo.startsWith("http://") ||
      photo.startsWith("https://") ||
      photo.startsWith("data:")
    ) {
      return photo;
    }

    // If backend returns /uploads/...
    if (photo.startsWith("/")) {
      return photo;
    }

   return `https://dgps.co.in/${photo}`;
  };

  // =====================================================
  // LOAD CLASSES
  // =====================================================

  const fetchClasses = async () => {
    setLoadingClasses(true);

    try {
      const response = await api.get("/class-teacher/classes");

      if (!isSuccess(response)) {
        throw new Error("Unable to load classes");
      }

      const list = getArray(response);

      setClasses(list);
    } catch (error) {
      console.error("Classes error:", error);
      setClasses([]);
      showToast("error", "Failed to load classes.");
    } finally {
      setLoadingClasses(false);
    }
  };

  // =====================================================
  // LOAD SECTIONS
  // =====================================================

  const fetchSections = async (classId) => {
    if (!classId) {
      setSections([]);
      return;
    }

    setLoadingSections(true);

    try {
      const response = await api.get("/class-teacher/sections", {
        params: {
          class_id: classId,
        },
      });

      if (!isSuccess(response)) {
        throw new Error("Unable to load sections");
      }

      const list = getArray(response);

      setSections(list);
    } catch (error) {
      console.error("Sections error:", error);
      setSections([]);
      showToast("error", "Failed to load sections.");
    } finally {
      setLoadingSections(false);
    }
  };

  // =====================================================
  // LOAD TEACHERS
  // =====================================================

  const fetchTeachers = async () => {
    setLoadingTeachers(true);

    try {
      const response = await api.get("/class-teacher/teachers");

      if (!isSuccess(response)) {
        throw new Error("Unable to load teachers");
      }

      const list = getArray(response);

      const normalized = list.map(normalizeTeacher);

      setTeachers(normalized);
    } catch (error) {
      console.error("Teachers error:", error);
      setTeachers([]);
      showToast("error", "Failed to load teachers.");
    } finally {
      setLoadingTeachers(false);
    }
  };

  // =====================================================
  // LOAD ALL ASSIGNMENTS
  // =====================================================

  const fetchAssignments = async () => {
    setLoadingAssignments(true);

    try {
      const response = await api.get("/class-teacher/assignments");

      if (!isSuccess(response)) {
        throw new Error("Unable to load assignments");
      }

      const list = getArray(response);

      setAssignments(list);
    } catch (error) {
      console.error("Assignments error:", error);

      setAssignments([]);

      // Empty assignment table should not show error
      if (
        error?.response?.status !== 404 &&
        error?.response?.status !== 204
      ) {
        showToast("error", "Failed to load assigned class teachers.");
      }
    } finally {
      setLoadingAssignments(false);
    }
  };

  // =====================================================
  // LOAD CURRENT ASSIGNMENT
  // =====================================================

  const fetchCurrentAssignment = async (classId, sectionId) => {
    if (!classId || !sectionId) {
      return;
    }

    try {
      const response = await api.get("/class-teacher/assignment", {
        params: {
          class_id: classId,
          section_id: sectionId,
        },
      });

      /*
       Backend may return:

       {
         success: true,
         data: {...}
       }

       OR

       {
         success: true,
         data: [...]
       }
      */

      if (!response?.data) {
        return;
      }

      const rawData = response.data.data;

      let assignment = null;

      if (Array.isArray(rawData)) {
        assignment = rawData.length > 0 ? rawData[0] : null;
      } else if (rawData && typeof rawData === "object") {
        assignment = rawData;
      }

      if (!assignment) {
        setForm((prev) => ({
          ...prev,
          teacher_id: null,
        }));

        setEditingAssignment(null);

        return;
      }

      const teacherId = getValue(
        assignment,
        [
          "teacher_id",
          "staff_id",
          "teacherId",
          "assigned_teacher_id",
        ],
        null
      );

      setForm((prev) => ({
        ...prev,
        teacher_id: teacherId ? String(teacherId) : null,
      }));

      setEditingAssignment(assignment);
    } catch (error) {
      /*
       404 means there is no assignment.
       That is NOT an error for this page.
      */

      if (error?.response?.status === 404) {
        setForm((prev) => ({
          ...prev,
          teacher_id: null,
        }));

        setEditingAssignment(null);

        return;
      }

      console.error("Current assignment error:", error);
    }
  };

  // =====================================================
  // INITIAL LOAD
  // =====================================================

  useEffect(() => {
    fetchClasses();
    fetchTeachers();
    fetchAssignments();
  }, []);

  // =====================================================
  // CLASS OPTIONS
  // =====================================================

  const classOptions = useMemo(() => {
    return classes.map((item) => ({
      value: getValue(item, ["id", "class_id"]),
      label: getValue(
        item,
        ["class_name", "name", "className"],
        "Unknown Class"
      ),
    }));
  }, [classes]);

  // =====================================================
  // SECTION OPTIONS
  // =====================================================

  const sectionOptions = useMemo(() => {
    return sections.map((item) => ({
      value: getValue(item, ["id", "section_id"]),
      label: getValue(
        item,
        ["section_name", "name", "sectionName"],
        "Unknown Section"
      ),
    }));
  }, [sections]);

  // =====================================================
  // TEACHER SEARCH
  // =====================================================

  const filteredTeachers = useMemo(() => {
    const search = teacherSearch.trim().toLowerCase();

    if (!search) {
      return teachers;
    }

    return teachers.filter((teacher) => {
      const name = String(teacher.name || "").toLowerCase();

      const mobile = String(teacher.mobile || "").toLowerCase();

      const teacherId = String(teacher.id || "").toLowerCase();

      const teacherCode = String(
        teacher.teacherCode || ""
      ).toLowerCase();

      return (
        name.includes(search) ||
        mobile.includes(search) ||
        teacherId.includes(search) ||
        teacherCode.includes(search)
      );
    });
  }, [teachers, teacherSearch]);

  // =====================================================
  // CUSTOM TEACHER OPTION
  // =====================================================

  const TeacherOption = (props) => {
    const {
      data,
      innerRef,
      innerProps,
      isFocused,
    } = props;

    const photo = getTeacherPhoto(data);

    return (
      <div
        ref={innerRef}
        {...innerProps}
        className={`ct-teacher-option ${
          isFocused ? "ct-teacher-option-focused" : ""
        }`}
      >
        <div className="ct-teacher-option-photo">
          {photo ? (
            <img
              src={photo}
              alt={data.name}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <FaUser />
          )}
        </div>

        <div className="ct-teacher-option-info">
          <div className="ct-teacher-option-name">
            {data.name}
          </div>

          <div className="ct-teacher-option-details">
            <span>
              <FaMobileAlt />
              {data.mobile || "No mobile"}
            </span>

            <span>
              <FaIdCard />
              ID: {data.teacherCode || data.id || "-"}
            </span>
          </div>

          <div className="ct-teacher-option-designation">
            {data.designation || "Teacher"}
          </div>
        </div>
      </div>
    );
  };

  // =====================================================
  // CUSTOM SELECTED VALUE
  // =====================================================

  const TeacherSingleValue = (props) => {
    const { data } = props;

    const photo = getTeacherPhoto(data);

    return (
      <div className="ct-teacher-single">
        <div className="ct-teacher-single-photo">
          {photo ? (
            <img src={photo} alt={data.name} />
          ) : (
            <FaUser />
          )}
        </div>

        <div className="ct-teacher-single-info">
          <strong>{data.name}</strong>

          <small>
            {data.mobile || "No mobile"}{" "}
            {data.teacherCode
              ? `• ID: ${data.teacherCode}`
              : ""}
          </small>
        </div>
      </div>
    );
  };

  // =====================================================
  // TEACHER SELECT OPTIONS
  // =====================================================

  const teacherOptions = useMemo(() => {
    return filteredTeachers.map((teacher) => ({
      ...teacher,
      value: String(teacher.id),
      label: teacher.name,
    }));
  }, [filteredTeachers]);

  // =====================================================
  // SELECTED TEACHER
  // =====================================================

  const selectedTeacher = useMemo(() => {
    if (!form.teacher_id) return null;

    return (
      teacherOptions.find(
        (teacher) =>
          String(teacher.id) === String(form.teacher_id)
      ) || null
    );
  }, [form.teacher_id, teacherOptions]);

  // =====================================================
  // CLASS CHANGE
  // =====================================================

  const handleClassChange = async (selected) => {
    const classId = selected?.value || null;

    setForm({
      class_id: classId,
      section_id: null,
      teacher_id: null,
    });

    setSections([]);
    setEditingAssignment(null);

    if (classId) {
      await fetchSections(classId);
    }
  };

  // =====================================================
  // SECTION CHANGE
  // =====================================================

  const handleSectionChange = async (selected) => {
    const sectionId = selected?.value || null;

    setForm((prev) => ({
      ...prev,
      section_id: sectionId,
      teacher_id: null,
    }));

    setEditingAssignment(null);

    if (form.class_id && sectionId) {
      await fetchCurrentAssignment(
        form.class_id,
        sectionId
      );
    }
  };

  // =====================================================
  // TEACHER CHANGE
  // =====================================================

  const handleTeacherChange = (selected) => {
    setForm((prev) => ({
      ...prev,
      teacher_id: selected?.value || null,
    }));
  };

  // =====================================================
  // SAVE / UPDATE
  // =====================================================

  const handleSave = async () => {
    if (!form.class_id) {
      showToast("warning", "Please select a class.");
      return;
    }

    if (!form.section_id) {
      showToast("warning", "Please select a section.");
      return;
    }

    if (!form.teacher_id) {
      showToast("warning", "Please select a class teacher.");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        class_id: Number(form.class_id),
        section_id: Number(form.section_id),
        teacher_id: Number(form.teacher_id),
      };

      /*
       IMPORTANT:

       Backend will automatically use current
       academic year.

       No academic_year_id is sent from frontend.
      */

      const response = await api.post(
        "/class-teacher/save",
        payload
      );

      if (
        response?.data?.success === false
      ) {
        throw new Error(
          response?.data?.message ||
            "Unable to save assignment"
        );
      }

      showToast(
        "success",
        editingAssignment
          ? "Class teacher updated successfully."
          : "Class teacher assigned successfully."
      );

      await fetchAssignments();

      await fetchCurrentAssignment(
        form.class_id,
        form.section_id
      );

    } catch (error) {
      console.error("Save assignment error:", error);

      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to save class teacher.";

      showToast("error", message);
    } finally {
      setSaving(false);
    }
  };

  // =====================================================
  // EDIT ASSIGNMENT
  // =====================================================

  const handleEdit = async (assignment) => {
    const classId = getValue(
      assignment,
      ["class_id", "classId"],
      null
    );

    const sectionId = getValue(
      assignment,
      ["section_id", "sectionId"],
      null
    );

    const teacherId = getValue(
      assignment,
      [
        "teacher_id",
        "staff_id",
        "teacherId",
        "assigned_teacher_id",
      ],
      null
    );

    if (!classId || !sectionId) {
      showToast(
        "error",
        "Class or section information is missing."
      );

      return;
    }

    setEditingAssignment(assignment);

    setForm({
      class_id: classId,
      section_id: sectionId,
      teacher_id: teacherId
        ? String(teacherId)
        : null,
    });

    await fetchSections(classId);

    /*
     Teacher search reset
    */

    setTeacherSearch("");

    /*
     Scroll page to assignment form
    */

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    /*
     If backend assignment response does not contain
     teacher_id, fetch it again.
    */

    if (!teacherId) {
      await fetchCurrentAssignment(
        classId,
        sectionId
      );
    }
  };

  // =====================================================
  // REMOVE ASSIGNMENT
  // =====================================================

  const handleRemove = async (assignment) => {
    const assignmentId = getValue(
      assignment,
      ["id", "assignment_id"],
      null
    );

    if (!assignmentId) {
      showToast(
        "error",
        "Assignment ID not found."
      );

      return;
    }

    const className = getValue(
      assignment,
      ["class_name", "className"],
      "this class"
    );

    const sectionName = getValue(
      assignment,
      ["section_name", "sectionName"],
      ""
    );

    const teacherName = getValue(
      assignment,
      [
        "teacher_name",
        "teacherName",
        "staff_name",
      ],
      "this teacher"
    );

    const confirmed = window.confirm(
      `Remove ${teacherName} as class teacher of ${className}${
        sectionName ? ` - ${sectionName}` : ""
      }?`
    );

    if (!confirmed) {
      return;
    }

    setRemovingId(assignmentId);

    try {
      const response = await api.post(
        "/class-teacher/remove",
        {
          id: Number(assignmentId),
        }
      );

      if (
        response?.data?.success === false
      ) {
        throw new Error(
          response?.data?.message ||
            "Unable to remove assignment"
        );
      }

      showToast(
        "success",
        "Class teacher assignment removed."
      );

      await fetchAssignments();

      if (
        form.class_id &&
        form.section_id
      ) {
        await fetchCurrentAssignment(
          form.class_id,
          form.section_id
        );
      }
    } catch (error) {
      console.error(
        "Remove assignment error:",
        error
      );

      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to remove assignment.";

      showToast("error", message);
    } finally {
      setRemovingId(null);
    }
  };

  // =====================================================
  // CANCEL EDIT
  // =====================================================

  const handleCancelEdit = () => {
    setEditingAssignment(null);

    setForm({
      class_id: null,
      section_id: null,
      teacher_id: null,
    });

    setSections([]);
    setTeacherSearch("");
  };

  // =====================================================
  // REFRESH
  // =====================================================

  const handleRefresh = async () => {
    await Promise.all([
      fetchClasses(),
      fetchTeachers(),
      fetchAssignments(),
    ]);

    if (
      form.class_id &&
      form.section_id
    ) {
      await fetchSections(form.class_id);

      await fetchCurrentAssignment(
        form.class_id,
        form.section_id
      );
    }
  };

  // =====================================================
  // SELECTED CLASS / SECTION
  // =====================================================

  const selectedClass =
    classOptions.find(
      (item) =>
        String(item.value) ===
        String(form.class_id)
    ) || null;

  const selectedSection =
    sectionOptions.find(
      (item) =>
        String(item.value) ===
        String(form.section_id)
    ) || null;

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <div className="class-teacher-container">

      <ToastContainer />

      {/* =================================================
          HEADER
      ================================================= */}

      <div className="ct-page-header">

        <div className="ct-page-header-left">

          <div className="ct-page-icon">
            <FaChalkboardTeacher />
          </div>

          <div>
            <h2>
              Class Teacher Assignment
            </h2>

            <p>
              Assign one teacher to each class and section
            </p>
          </div>

        </div>

        <button
          type="button"
          className="ct-refresh-btn"
          onClick={handleRefresh}
          disabled={
            loadingClasses ||
            loadingTeachers ||
            loadingAssignments
          }
        >
          <FaSyncAlt
            className={
              loadingClasses ||
              loadingTeachers ||
              loadingAssignments
                ? "ct-spin"
                : ""
            }
          />

          Refresh
        </button>

      </div>

      {/* =================================================
          ASSIGNMENT FORM CARD
      ================================================= */}

      <div className="ct-card">

        <div className="ct-card-header">

          <div className="ct-card-header-icon">
            <FaUserTie />
          </div>

          <div>
            <h3>
              {editingAssignment
                ? "Update Class Teacher"
                : "Assign Class Teacher"}
            </h3>

            <p>
              Select class, section and teacher
            </p>
          </div>

        </div>

        <div className="ct-divider" />

        {/* =================================================
            FORM ROW
        ================================================= */}

        <div className="ct-form-grid">

          {/* CLASS */}

          <div className="ct-field">

            <label>
              Select Class
              <span>*</span>
            </label>

            <Select
              value={selectedClass}
              options={classOptions}
              onChange={handleClassChange}
              isClearable
              isLoading={loadingClasses}
              placeholder="Select Class"
              classNamePrefix="ct-select"
            />

          </div>

          {/* SECTION */}

          <div className="ct-field">

            <label>
              Select Section
              <span>*</span>
            </label>

            <Select
              value={selectedSection}
              options={sectionOptions}
              onChange={handleSectionChange}
              isClearable
              isDisabled={!form.class_id}
              isLoading={loadingSections}
              placeholder={
                form.class_id
                  ? "Select Section"
                  : "Select Class First"
              }
              classNamePrefix="ct-select"
            />

          </div>

          {/* TEACHER */}

          <div className="ct-field ct-teacher-field">

            <label>
              Select Class Teacher
              <span>*</span>
            </label>

            <div className="ct-teacher-search-box">

              <FaSearch />

              <input
                type="text"
                value={teacherSearch}
                onChange={(e) =>
                  setTeacherSearch(
                    e.target.value
                  )
                }
                placeholder="Search teacher by name, mobile or ID..."
                disabled={!form.section_id}
              />

              {teacherSearch && (
                <button
                  type="button"
                  onClick={() =>
                    setTeacherSearch("")
                  }
                  className="ct-search-clear"
                >
                  <FaTimes />
                </button>
              )}

            </div>

            <Select
              value={selectedTeacher}
              options={teacherOptions}
              onChange={handleTeacherChange}
              isClearable
              isDisabled={!form.section_id}
              isLoading={loadingTeachers}
              placeholder={
                form.section_id
                  ? "Select Class Teacher"
                  : "Select Section First"
              }
              classNamePrefix="ct-select"
              components={{
                Option: TeacherOption,
                SingleValue: TeacherSingleValue,
              }}
              filterOption={null}
              noOptionsMessage={() =>
                teacherSearch
                  ? "No teacher found"
                  : "No teachers available"
              }
            />

            <small className="ct-search-help">
              Search by teacher name, mobile number or teacher ID
            </small>

          </div>

        </div>

        {/* =================================================
            SELECTED SUMMARY
        ================================================= */}

        {form.class_id &&
          form.section_id && (
            <div className="ct-assignment-summary">

              <div className="ct-summary-title">
                Assignment Summary
              </div>

              <div className="ct-summary-grid">

                <div className="ct-summary-item">
                  <span>Class</span>
                  <strong>
                    {selectedClass?.label || "-"}
                  </strong>
                </div>

                <div className="ct-summary-item">
                  <span>Section</span>
                  <strong>
                    {selectedSection?.label || "-"}
                  </strong>
                </div>

                <div className="ct-summary-item ct-summary-teacher">

                  <span>
                    Class Teacher
                  </span>

                  {selectedTeacher ? (
                    <div className="ct-selected-teacher">

                      <div className="ct-selected-photo">

                        {getTeacherPhoto(
                          selectedTeacher
                        ) ? (
                          <img
                            src={getTeacherPhoto(
                              selectedTeacher
                            )}
                            alt={
                              selectedTeacher.name
                            }
                          />
                        ) : (
                          <FaUser />
                        )}

                      </div>

                      <div>
                        <strong>
                          {selectedTeacher.name}
                        </strong>

                        <small>
                          {selectedTeacher.mobile ||
                            "No mobile"}

                          {selectedTeacher.teacherCode
                            ? ` • ID: ${selectedTeacher.teacherCode}`
                            : ""}
                        </small>
                      </div>

                    </div>
                  ) : (
                    <strong className="ct-not-selected">
                      Not selected
                    </strong>
                  )}

                </div>

              </div>

            </div>
          )}

        {/* =================================================
            BUTTONS
        ================================================= */}

        <div className="ct-form-actions">

          {editingAssignment && (
            <button
              type="button"
              className="ct-cancel-btn"
              onClick={handleCancelEdit}
              disabled={saving}
            >
              <FaTimes />
              Cancel
            </button>
          )}

          <button
            type="button"
            className="ct-save-btn"
            onClick={handleSave}
            disabled={
              saving ||
              !form.class_id ||
              !form.section_id ||
              !form.teacher_id
            }
          >
            {saving ? (
              <>
                <FaSyncAlt className="ct-spin" />
                Saving...
              </>
            ) : (
              <>
                {editingAssignment ? (
                  <FaEdit />
                ) : (
                  <FaSave />
                )}

                {editingAssignment
                  ? "Update Class Teacher"
                  : "Assign Class Teacher"}
              </>
            )}
          </button>

        </div>

      </div>

      {/* =================================================
          ASSIGNED CLASS TEACHERS
      ================================================= */}

      <div className="ct-card ct-assigned-card">

        <div className="ct-card-header">

          <div className="ct-card-header-icon">
            <FaChalkboardTeacher />
          </div>

          <div>
            <h3>
              Assigned Class Teachers
            </h3>

            <p>
              All class teacher assignments
            </p>
          </div>

          <div className="ct-count-chip">
            {assignments.length} Assigned
          </div>

        </div>

        <div className="ct-divider" />

        {loadingAssignments ? (
          <div className="ct-loading">
            <FaSyncAlt className="ct-spin" />
            <span>
              Loading assignments...
            </span>
          </div>
        ) : assignments.length === 0 ? (
          <div className="ct-empty">

            <div className="ct-empty-icon">
              <FaChalkboardTeacher />
            </div>

            <h4>
              No class teacher assigned yet
            </h4>

            <p>
              Select a class, section and teacher above
              to create an assignment.
            </p>

          </div>
        ) : (
          <div className="ct-table-wrapper">

            <table className="ct-table">

              <thead>
                <tr>
                  <th>#</th>
                  <th>Class</th>
                  <th>Section</th>
                  <th>Teacher</th>
                  <th>Mobile</th>
                  <th>Teacher ID</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>

                {assignments.map(
                  (assignment, index) => {

                    const teacherPhoto =
                      getValue(
                        assignment,
                        [
                          "teacher_photo",
                          "photo",
                          "teacher_image",
                          "profile_photo",
                          "teacher_photo_url",
                        ],
                        ""
                      );

                    const teacherName =
                      getValue(
                        assignment,
                        [
                          "teacher_name",
                          "teacherName",
                          "staff_name",
                          "name",
                        ],
                        "-"
                      );

                    const teacherMobile =
                      getValue(
                        assignment,
                        [
                          "teacher_mobile",
                          "mobile",
                          "mobile_no",
                          "phone",
                          "phone_no",
                        ],
                        "-"
                      );

                    const teacherCode =
                      getValue(
                        assignment,
                        [
                          "teacher_code",
                          "teacher_id_no",
                          "employee_code",
                          "staff_code",
                          "teacher_id",
                        ],
                        "-"
                      );

                    const className =
                      getValue(
                        assignment,
                        [
                          "class_name",
                          "className",
                        ],
                        "-"
                      );

                    const sectionName =
                      getValue(
                        assignment,
                        [
                          "section_name",
                          "sectionName",
                        ],
                        "-"
                      );

                    const assignmentId =
                      getValue(
                        assignment,
                        [
                          "id",
                          "assignment_id",
                        ],
                        null
                      );

                    let photoUrl = teacherPhoto;

                    if (
                      photoUrl &&
                      !photoUrl.startsWith(
                        "http://"
                      ) &&
                      !photoUrl.startsWith(
                        "https://"
                      ) &&
                      !photoUrl.startsWith(
                        "data:"
                      ) &&
                      !photoUrl.startsWith("/")
                    ) {
                      photoUrl =
                        "/" + photoUrl;
                    }

                    return (
                      <tr
                        key={
                          assignmentId ||
                          `${className}-${sectionName}-${index}`
                        }
                      >

                        <td>
                          {index + 1}
                        </td>

                        <td>
                          <strong>
                            {className}
                          </strong>
                        </td>

                        <td>
                          <span className="ct-section-badge">
                            {sectionName}
                          </span>
                        </td>

                        <td>

                          <div className="ct-table-teacher">

                            <div className="ct-table-photo">

                              {photoUrl ? (
                                <img
                                  src={photoUrl}
                                  alt={teacherName}
                                  onError={(e) => {
                                    e.currentTarget.style.display =
                                      "none";
                                  }}
                                />
                              ) : (
                                <FaUser />
                              )}

                            </div>

                            <div>
                              <strong>
                                {teacherName}
                              </strong>

                              <small>
                                {getValue(
                                  assignment,
                                  [
                                    "designation",
                                    "designation_name",
                                    "teacher_designation",
                                  ],
                                  "Teacher"
                                )}
                              </small>
                            </div>

                          </div>

                        </td>

                        <td>
                          <span className="ct-mobile">
                            <FaMobileAlt />
                            {teacherMobile}
                          </span>
                        </td>

                        <td>
                          <span className="ct-teacher-id">
                            <FaIdCard />
                            {teacherCode}
                          </span>
                        </td>

                        <td>

                          <div className="ct-action-buttons">

                            <button
                              type="button"
                              className="ct-edit-btn"
                              onClick={() =>
                                handleEdit(
                                  assignment
                                )
                              }
                            >
                              <FaEdit />
                              Edit
                            </button>

                            <button
                              type="button"
                              className="ct-delete-btn"
                              onClick={() =>
                                handleRemove(
                                  assignment
                                )
                              }
                              disabled={
                                removingId ===
                                assignmentId
                              }
                            >
                              {removingId ===
                              assignmentId ? (
                                <FaSyncAlt className="ct-spin" />
                              ) : (
                                <FaTrash />
                              )}

                              Remove
                            </button>

                          </div>

                        </td>

                      </tr>
                    );
                  }
                )}

              </tbody>

            </table>

          </div>
        )}

      </div>

    </div>
  );
};

export default ClassTeacherPage;